import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import FunnelVisualization from './components/FunnelVisualization';
import ConversionMetricCard from './components/ConversionMetricCard';
import CohortRetentionTable from './components/CohortRetentionTable';
import FeatureAdoptionChart from './components/FeatureAdoptionChart';
import SegmentationAnalysis from './components/SegmentationAnalysis';
import ConversionFilters from './components/ConversionFilters';
import analyticsService from '../../services/analyticsService';
import { logger } from '../../utils/logger';
import { format, subDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';


const PremiumConversionFunnelAnalytics = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [error, setError] = useState('');
  
  // Filter states
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedCohort, setSelectedCohort] = useState('all');

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange, selectedSegment, selectedCohort]);

  // Check officer access
  const hasAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.CONVERSION_FUNNEL_ANALYTICS);

  // Redirect non-officers
  useEffect(() => {
    if (!loading && userProfile && !hasAccess) {
      navigate('/settings-and-privacy');
    }
  }, [hasAccess, loading, userProfile, navigate]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    setError('');

    try {
      const { data, error: analyticsError } = await analyticsService?.getConversionFunnelAnalytics({
        dateRange,
        segment: selectedSegment !== 'all' ? selectedSegment : null,
        cohort: selectedCohort !== 'all' ? selectedCohort : null
      });

      if (analyticsError) {
        setError(analyticsError?.message || 'Failed to load analytics data');
        logger?.error('Failed to load conversion analytics', analyticsError);
      } else {
        setAnalyticsData(data);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      logger?.error('Analytics load error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const { data, error } = await analyticsService?.exportConversionReport({
        dateRange,
        segment: selectedSegment !== 'all' ? selectedSegment : null,
        cohort: selectedCohort !== 'all' ? selectedCohort : null
      });

      if (error) {
        logger?.error('Failed to export report', error);
        return;
      }

      // Create download link
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window?.URL?.createObjectURL(blob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = `conversion-funnel-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document?.body?.appendChild(link);
      link?.click();
      document?.body?.removeChild(link);
      window?.URL?.revokeObjectURL(url);

      logger?.info('Conversion analytics report exported');
    } catch (err) {
      logger?.error('Export error', err);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Premium Conversion Funnel Analytics - MyBodyToday</title>
          <meta name="description" content="Comprehensive conversion funnel analysis and monetization optimization insights" />
        </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center pb-20">
          <div className="flex flex-col items-center gap-4">
            <Icon name="Loader2" size={40} className="text-primary animate-spin" strokeWidth={2} />
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
        <BottomTabNavigation />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Premium Conversion Funnel Analytics - MyBodyToday</title>
        <meta name="description" content="Comprehensive conversion funnel analysis and monetization optimization insights" />
      </Helmet>

      <div className="min-h-screen bg-background pb-20">
        {/* Header */}
        <div className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
                  Premium Conversion Funnel Analytics
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Optimize monetization with detailed conversion pathway analysis and upgrade opportunity identification
                </p>
              </div>
              <button
                onClick={handleExportReport}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium transition-smooth hover:bg-primary/90 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background active:scale-[0.97]"
                aria-label="Export conversion analytics report"
              >
                <Icon name="Download" size={18} strokeWidth={2} />
                <span className="hidden sm:inline">Export Report</span>
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg text-error text-sm">
              {error}
            </div>
          )}

          {/* Filter Controls */}
          <ConversionFilters
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            selectedSegment={selectedSegment}
            onSegmentChange={setSelectedSegment}
            selectedCohort={selectedCohort}
            onCohortChange={setSelectedCohort}
          />

          {/* Key Conversion Metrics */}
          {analyticsData && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <ConversionMetricCard
                  icon="TrendingUp"
                  label="Overall Conversion Rate"
                  value={`${analyticsData?.overallConversionRate || 0}%`}
                  trend={analyticsData?.conversionTrend}
                  color="primary"
                />
                <ConversionMetricCard
                  icon="Users"
                  label="Trial-to-Paid Conversion"
                  value={`${analyticsData?.trialConversionRate || 0}%`}
                  trend={analyticsData?.trialTrend}
                  color="success"
                />
                <ConversionMetricCard
                  icon="DollarSign"
                  label="Average Revenue Per User"
                  value={`$${analyticsData?.arpu || 0}`}
                  trend={analyticsData?.arpuTrend}
                  color="warning"
                />
                <ConversionMetricCard
                  icon="Target"
                  label="Feature Gate Conversion"
                  value={`${analyticsData?.featureGateConversion || 0}%`}
                  trend={analyticsData?.featureGateTrend}
                  color="error"
                />
              </div>

              {/* Funnel Visualization */}
              <div className="mb-8">
                <FunnelVisualization data={analyticsData?.funnelStages} />
              </div>

              {/* Feature Adoption Chart */}
              <div className="mb-8">
                <FeatureAdoptionChart data={analyticsData?.featureAdoption} />
              </div>

              {/* Segmentation Analysis */}
              <div className="mb-8">
                <SegmentationAnalysis data={analyticsData?.segmentAnalysis} />
              </div>

              {/* Cohort Retention Table */}
              <div className="mb-8">
                <CohortRetentionTable data={analyticsData?.cohortRetention} />
              </div>
            </>
          )}
        </div>
      </div>

      <BottomTabNavigation />
    </>
  );
};

export default PremiumConversionFunnelAnalytics;